import type { Scene } from '@lumen5/luminary';
import { AspectRatio, VideoData } from '@lumen5/luminary';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { resolveSceneHandle, sceneDisplayId } from './scene-addressing';
import { VideoStateTool } from './VideoStateTool';

import type { TransitionOption } from 'js/src/components/app/transitions-sidebar/transition-data';
import {
    getTransitionOptions,
    getTransitionUrlForOption,
    NONE_TRANSITION_URL,
    TRANSITION_GROUP_IDS,
    TRANSITION_LABELS,
    TRANSITION_URL_DATA,
} from 'js/src/components/app/transitions-sidebar/transition-data';
import { applyTransitionOperation } from 'js/src/components/app/transitions-sidebar/transition-operations';
import { checkpointVideo } from 'js/src/data/video-data';
import Flags from 'js/src/libs/flags';
import type { HtmlAsset } from 'js/src/luminary/html-asset-operations';
import {
    saveRawLuminary,
    setVideoTransitionsEnabledOperation,
    syncMatchCutsWithTransitionsEnabledOperation,
} from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import {
    hasAppliedMatchCutWithNextScene,
    hasAppliedTransition,
} from 'js/src/luminary/luminary-selectors';

const NO_TRANSITION = 'none';

// Every group ships all four aspect ratios, so any one of them lists the group's variants.
const VARIANTS_BY_LABEL: ReadonlyMap<string, TransitionOption[]> = new Map(
    TRANSITION_GROUP_IDS.map((groupId) => [
        TRANSITION_LABELS[groupId],
        getTransitionOptions(groupId, AspectRatio.LANDSCAPE).map(({ option }) => option),
    ]),
);
const GROUP_ID_BY_LABEL: ReadonlyMap<string, string> = new Map(
    TRANSITION_GROUP_IDS.map((groupId) => [TRANSITION_LABELS[groupId], groupId]),
);
const TRANSITION_CHOICES = [NO_TRANSITION, ...VARIANTS_BY_LABEL.keys()] as [string, ...string[]];
const VARIANT_CHOICES = [...new Set([...VARIANTS_BY_LABEL.values()].flat())] as [
    TransitionOption,
    ...TransitionOption[],
];

const variantsText = (label: string): string => (VARIANTS_BY_LABEL.get(label) ?? []).join(', ');

export const setSceneTransitionSchema = z.object({
    scene_id: z
        .string()
        .describe(
            'The scene_id, from the video state, of the scene the transition plays INTO — the second of the two scenes it joins.',
        ),
    transition: z
        .enum(TRANSITION_CHOICES)
        .describe(
            `The transition to play into the scene, or "${NO_TRANSITION}" for a straight cut.`,
        ),
    variant: z
        .enum(VARIANT_CHOICES)
        .optional()
        .describe("The transition's direction, shape or length; omit for its default."),
});
export type SetSceneTransitionArgs = z.infer<typeof setSceneTransitionSchema>;

function hasAuthoredGraphic(scene: Scene): boolean {
    const asset = scene.getAssetsByType(VideoData.HTML_ASSET_TYPE)[0] as HtmlAsset | undefined;
    return !!asset?.html;
}

export class SetSceneTransitionTool extends VideoStateTool<typeof setSceneTransitionSchema> {
    protected override readonly schema = setSceneTransitionSchema;
    protected override readonly toolName = TOOL_NAMES.setSceneTransition;
    protected override readonly toolDescription =
        'Sets the transition that plays into a scene from the one before it, or removes it for a straight cut. A transition overlaps the two scenes by up to a second, which starts the scene it leads into that much earlier against its narration.';

    public override getToolDisplayName(): string {
        return 'Setting scene transition';
    }

    public get statusIcon(): string {
        return 'transition_fade';
    }

    public async execute(args: SetSceneTransitionArgs, context: ToolExecutionContext) {
        const { transition, variant } = args;
        const { parentActionGroup } = context;

        try {
            const { scene, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );
            const prevScene = scene.prevScene;
            if (!prevScene) {
                return `Error: Scene ${sceneShortId} is the first scene in the video, so there is no scene before it to transition from. Pass the second of the two scenes you want to join.`;
            }
            const prevShortId = sceneDisplayId(this.luminary, prevScene.id);
            const hadTransition = hasAppliedTransition(scene);
            const transitionsWereEnabled = this.luminary.template.transitionsEnabled;

            let transitionUrl: string | null = NONE_TRANSITION_URL;
            if (transition !== NO_TRANSITION) {
                const variants = VARIANTS_BY_LABEL.get(transition) ?? [];
                if (variant && !variants.includes(variant)) {
                    return `Error: ${transition} has no "${variant}" variant. Its variants are ${variantsText(transition)}.`;
                }
                if (
                    !transitionsWereEnabled &&
                    prevScene.matchCutTechnique !== VideoData.MATCH_CUT_NONE &&
                    !prevScene.isPrePackagedMorphWithNextScene
                ) {
                    return `Error: Scene transitions are turned off for this whole video, and switching them back on for this transition would also bring back the match cut saved from scene ${prevShortId} into scene ${sceneShortId}, which a seam cannot hold alongside it. Leave this seam as it is.`;
                }
                if (prevScene.isPrePackagedMorphWithNextScene) {
                    return `Error: Scenes ${prevShortId} and ${sceneShortId} belong to a pre-built scene sequence, which already animates between its scenes as it was designed to.`;
                }
                if (hasAppliedMatchCutWithNextScene(prevScene)) {
                    return `Error: Scene ${prevShortId} match cuts into scene ${sceneShortId}, and a seam holds one or the other. Remove the match cut first with ${TOOL_NAMES.setMatchCut} (scene_id "${prevShortId}", enabled: false).`;
                }
                if (prevScene.morphEnabled) {
                    return `Error: The seam between scenes ${prevShortId} and ${sceneShortId} already animates through another feature, such as an animated zoom, and a transition cannot share a seam with it.`;
                }
                const groupId = GROUP_ID_BY_LABEL.get(transition) as string;
                transitionUrl = getTransitionUrlForOption(
                    groupId,
                    this.luminary.aspectRatio,
                    variant,
                );
                if (!transitionUrl) {
                    throw new Error(`No ${transition} transition for this aspect ratio`);
                }
                if (
                    transitionsWereEnabled &&
                    hadTransition &&
                    scene.customTransition.transitionUrl === transitionUrl
                ) {
                    return `Success: Scene ${sceneShortId} already opens with a ${transition} (${TRANSITION_URL_DATA[transitionUrl]?.optionId}) transition from scene ${prevShortId}.`;
                }
            } else if (hasAppliedMatchCutWithNextScene(prevScene)) {
                return `Success: There is no transition into scene ${sceneShortId}. The seam carries a match cut from scene ${prevShortId} instead, which ${TOOL_NAMES.setMatchCut} removes.`;
            } else if (!hadTransition) {
                if (prevScene.isPrePackagedMorphWithNextScene) {
                    return `Success: There is no transition into scene ${sceneShortId}. Scenes ${prevShortId} and ${sceneShortId} belong to a pre-built scene sequence, which animates between its scenes as it was designed to.`;
                }
                if (prevScene.morphEnabled) {
                    return `Success: There is no transition into scene ${sceneShortId}, but the seam from scene ${prevShortId} already animates through another feature, such as an animated zoom, which this tool does not change.`;
                }
                return `Success: Scene ${sceneShortId} already cuts straight from scene ${prevShortId}.`;
            }

            const {
                applyTransition,
                setVideoTransitionsEnabled,
                syncMatchCutsWithTransitionsEnabled,
            } = makeOperationsCallable(
                {
                    applyTransitionOperation,
                    setVideoTransitionsEnabledOperation,
                    syncMatchCutsWithTransitionsEnabledOperation,
                },
                this.luminary,
                this.dispatch,
                () => this.getState(),
            );
            const enablesTransitions = !!transitionUrl && !transitionsWereEnabled;
            if (enablesTransitions) {
                setVideoTransitionsEnabled(true, false, parentActionGroup);
                syncMatchCutsWithTransitionsEnabled(true, false, parentActionGroup);
            }
            applyTransition(scene.id, transitionUrl);
            this.dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(this.luminary),
                    systemTriggered: false,
                    parentActionGroup,
                }),
            );

            const retimeNote =
                hasAuthoredGraphic(scene) && Flags.canUseCodeo()
                    ? ` Scene ${sceneShortId}'s graphic was timed to its narration before this change moved its start, so re-time it against its narration with ${TOOL_NAMES.editHtmlAsset}.`
                    : '';
            if (!transitionUrl) {
                return `Success: Removed the transition into scene ${sceneShortId}; it now cuts straight from scene ${prevShortId}.${retimeNote}`;
            }
            const appliedVariant = TRANSITION_URL_DATA[transitionUrl]?.optionId;
            const enabledNote = enablesTransitions
                ? ' Scene transitions were off for the whole video, so this switched them back on, which also brings back any transitions and match cuts saved on other scenes.'
                : '';
            return `Success: Scene ${sceneShortId} now opens with a ${transition} (${appliedVariant}) transition from scene ${prevShortId}.${enabledNote}${retimeNote}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting the scene transition: ${errorMessage}`;
        }
    }
}
